import { motion } from "framer-motion";

const AttentionScreener = ({ data }) => {
  return (
    <motion.div key={"report"} initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1.0, y: 0, transition: { delay: 0.5 } }} exit={{ opacity: 0, y: 40 }}>
      <div style={{ ontSize: 14, marginTop: 30, marginRight: 30, textAlign: "right", color: "#777777" }}>
        Test Date: <span style={{ fontStyle: "italic" }}>{data.completedAt}</span>
      </div>

      <h1 style={{ marginBottom: 20, marginTop: 20, paddingRight: 30, paddingLeft: 30, textAlign: "center", fontSize: 42, color: "#2c3436" }}>
        Attention Screener
      </h1>
      <Template data={data} />
    </motion.div>
  );
};

export default AttentionScreener;

const Template = ({ data }) => {
  return (
    <div style={{ paddingTop: 30, paddingBottom: 30, paddingRight: 30, paddingLeft: 30 }}>
      {/* <h2 style={{ marginBottom: 20 }}>Summary</h2> */}
      <div style={{ backgroundColor: "#fff", borderRadius: 8, padding: 30, marginBottom: 20 }}>
        <div style={{ fontSize: 28, textAlign: "center", marginBottom: 20 }}>
          <span style={{ fontWeight: 900 }}>Your Score: </span>
          <span style={{ fontWeight: 300, fontStyle: "italic" }}>{data.attention.reportValues.title}</span>
        </div>
        <ScoreMeter bars={data.attention.reportValues.bars} />
        <div style={{ marginTop: 20, textAlign: "center", fontStyle: "italic" }}>{data.attention.reportValues.description}</div>
        <div style={{ marginTop: 20, textAlign: "center" }}>
          <a style={{ fontWeight: 600, color: "#008dd0" }} href={data.attention.reportValues.link.url} target="_blank" rel="noreferrer">
            {data.attention.reportValues.link.title}
          </a>
        </div>
      </div>
      <Paragraph subtitle="Attention helps you learn!">
        Attention controls the flow of information in and out of the mind. Students who can easily focus are more likely to show consistent work. And they will
        typically be able to persevere longer, even for subjects or activities they don’t enjoy very much.
      </Paragraph>

      <Paragraph subtitle="Smart kids can have weak Attention.">
        Students with weaker attention will have difficulty sustaining focus over time. They often miss key details when someone is speaking or make scattered
        mistakes on homework and tests. They might have difficulty starting a new task, or they might easily initiate but their mind starts to wander when they
        work for too long. Students with weaker Attention can be confusing because they can easily focus for activities they enjoy, such as watching a movie,
        playing a game or reading a good book. However, their mind takes them somewhere else for activities they don’t naturally enjoy. Their Attention is not
        something they can easily control, but they can sustain focus for longer if they use{" "}
        <a
          href="https://my.mindprintlearning.com/search?name=&academic_topics=&cognitive_skills=.30&interests=&product_types=14.15.16&age_range_min=3&age_range_max=21&order_by=bestfit_score+desc&uses_recommendations=&pills=.cognitive_skills%7C30"
          target="_blank"
          rel="noreferrer"
          style={{ fontWeight: 600, color: "#008dd0" }}
        >
          Attention Strategies
        </a>
        .
      </Paragraph>
      <Paragraph subtitle="">
        Attention is one of 10 cognitive skills that determine how a student learns most effectively. The full MindPrint Assessment measures 10 skills in the
        four cognitive domains of <b>Executive Functions</b> (Attention, Working Memory, Flexible Thinking), <b>Complex Reasoning</b> (Verbal, Abstract,
        Spatial), <b>Memory</b> (Verbal, Visual) and <b>Processing</b> (Visual Motor, Visual).
      </Paragraph>
      <Paragraph>
        This full set of skills explains why a student might be successful in some subjects while needing support in others. Each skill has an important role in
        learning, but it is the complex interaction between these 10 skills that often determines how easily a student will learn in any topic and how
        effectively they are able to adjust to challenges.
      </Paragraph>
      <Paragraph>
        Once a student’s strengths and needs across these 10 skills is understood, it will be easier to identify what subjects or careers they might find most
        enjoyable, understand why they have specific struggles, and teach strategies that will help them learn more successfully in and out of school.
      </Paragraph>

      {!data.attention.validity && (
        <div style={{ fontSize: 14, marginBottom: 20 }}>
          *The student's score was "invalid" on the task. Invalid scores can arise as a result of failing to respond, or responding too many times or too
          quickly. This could be for a number of reasons, including the student was interrupted, misunderstood the instructions, or has difficulty with the
          particular skill. In some cases, hardware malfunctions may also contribute. Re-testing of this skill is recommended after waiting a minimum of a two
          weeks. Please be aware that Toolbox results may be impacted by any invalid scores.
        </div>
      )}
      <div style={{ fontSize: 14 }}>
        <em>
          The recipient of this report acknowledges that the information provided is solely for educational and informational purposes and is not a professional
          diagnosis and is not to be construed or used as professional advice or recommendations. The recipient of this report acknowledges that Mindprint
          Learning, LLC is an educational service and information company and does not provide medical, psychological or professional diagnoses, advice,
          treatment or recommendations.
        </em>
      </div>
    </div>
  );
};

const Paragraph = ({ children, subtitle }) => {
  return (
    <div>
      <p style={{ marginBottom: 20 }}>
        <span style={{ fontWeight: 600 }}>{subtitle}</span> {children}
      </p>
    </div>
  );
};

const ScoreMeter = ({ bars }) => {
  const RAD = 4;

  const getScoreColor = () => {
    const ORANGE = "#DE8F44";
    const YELLOW = "#F1C64C";
    const GREEN = "#3D9F53";
    const BLUE = "#1181B5";

    let col = ORANGE;

    if (bars > 3) col = YELLOW;
    if (bars > 4) col = GREEN;
    if (bars > 8) col = BLUE;

    return col;
  };
  return (
    <>
      <div
        style={{
          width: "100%",
          marginTop: 10,
          marginBottom: 10,
          height: 80,
          backgroundColor: "#f5f6fa",
          // border: "solid 4px #f5f6fa",
          display: "flex",
          borderRadius: RAD,
          padding: 10,
          // paddingTop: 10,
          // paddingBottom: 10,
        }}
      >
        {new Array(12).fill(0).map((item, i) => {
          return (
            <div key={"score-meter-block-" + i} style={{ flex: 1, height: "100%", display: "flex" }}>
              <div style={{ flex: 1, height: "100%" }} />
              <div
                key={"score-indicator-" + i}
                style={{ borderRadius: RAD, backgroundColor: i > bars - 1 ? "#eaebef" : getScoreColor(), width: "100%", flex: 8 }}
              />
              <div style={{ flex: 1, height: "100%" }} />
            </div>
          );
        })}
      </div>
    </>
  );
};

/*

<P>The Mindprint Attention Screener&nbsp;provides an objective indication of a&nbsp;student&rsquo;s attention skill, a key executive function.</P>
      <P>
        Executive functions broadly refer to a set of skills that involve the mind&rsquo;s organization system. It&nbsp;includes the ability to act purposefully
        toward a goal, focus, make a decision, plan, and follow through. Succeeding in school, pursuing a hobby, and teamwork all&nbsp;depend on executive
        functions.
      </P>
      <P>
        This brief screener addresses&nbsp;two executive functions:&nbsp;<strong>attention</strong>. Attention controls the flow of information in and out of
        the mind. If a student is actively focusing, the quality of work will likely be consistent. In contrast,&nbsp;students who tune in and out&nbsp;often
        miss key details. While students might be able to easily&nbsp;pay attention for tasks they enjoy, such as a watching a television show or playing a
        game,&nbsp;Mindprint measures whether they can maintain consistent focus for tasks that they&nbsp;might not find fun or interesting, a key skill for
        successful learning.
      </P>

      <P>
        The Attention Screener is intended to help you get more comfortable with the Mindprint system, but <strong>the information is quite limited</strong>. If
        you would like to know your student's actual scores on these two tasks and get a complete picture of<span style={{ color: "#ff0000" }}>&nbsp;</span>
        {firstName}&rsquo;s overall learning strengths,&nbsp;{gender} will need to&nbsp;
        <span>take the Comprehensive Mindprint. The Comprehensive Mindprint&nbsp;will provide a detailed&nbsp;evaluation of </span>
        {firstName}
        <span>
          &rsquo;s attention and working memory. In addition, it&nbsp;assesses flexible thinking, a third executive function skill, as well as three areas of
          complex reasoning, long-term memory and processing efficiency. Taking the Comprehensive will ensure that executive functions challenges are not
          masking difficulties in other skills or difficulties in other skills are manifesting as what might look like challenges with executive functions.
        </span>
      </P>
      <P>
        Strategies to support attention and working memory are in {firstName}'s Toolbox or you can&nbsp;click&nbsp;
        <a
          href="https://my.mindprintlearning.com/toolbox/toolbox-demo/search?name=&amp;academic_topics=&amp;cognitive_skills=23.30&amp;interests=&amp;product_types=12&amp;age_range_min=3&amp;age_range_max=21&amp;order_by=bestfit_score+desc&amp;uses_recommendations=&amp;pills=cognitive_skills%7C30.cognitive_skills%7C23.&amp;page=1"
          target="_blank"
          rel="noreferrer"
        >
          here
        </a>
        .
      </P>

*/
